import type { UserRole } from './constants'

export interface Policy {
  id: string
  title: string
  version: string
  content: string
  requiredFor: UserRole[]
  lastUpdated: string
}

const POLICIES: Policy[] = [
  {
    id: 'nda',
    title: 'Non-Disclosure Agreement',
    version: '2.0',
    content: `# Non-Disclosure Agreement

This Non-Disclosure Agreement ("Agreement") is entered into between LUKHAS AI and the undersigned party.

## Confidential Information
All technical data, trade secrets, proprietary information, and business strategies shared during your engagement with LUKHAS AI are considered confidential.

## Obligations
- Maintain strict confidentiality of all proprietary information
- Use confidential information only for authorized purposes
- Return all materials upon termination of engagement

## Duration
This agreement remains in effect for 5 years following termination of engagement.`,
    requiredFor: ['staff', 'manager', 'admin', 'contractor'],
    lastUpdated: '2024-01-01',
  },
  {
    id: 'employee-handbook',
    title: 'Employee Handbook',
    version: '3.1',
    content: `# Employee Handbook

Welcome to LUKHAS AI. This handbook outlines our policies and procedures.

## Work Hours
Standard work hours are flexible within core hours of 10 AM - 4 PM.

## Benefits
- Health insurance
- 401(k) matching
- Professional development budget

## Code of Conduct
All employees are expected to maintain professional behavior and respect for colleagues.`,
    requiredFor: ['staff', 'manager', 'admin'],
    lastUpdated: '2024-01-15',
  },
  {
    id: 'code-of-conduct',
    title: 'Code of Conduct',
    version: '1.5',
    content: `# Code of Conduct

## Our Values
- Integrity
- Innovation
- Collaboration
- Respect

## Expected Behavior
- Treat all colleagues with respect
- Maintain professional communication
- Report concerns through proper channels

## Prohibited Behavior
- Harassment of any kind
- Discrimination
- Misuse of company resources`,
    requiredFor: ['staff', 'manager', 'admin'],
    lastUpdated: '2024-01-10',
  },
  {
    id: 'contractor-agreement',
    title: 'Contractor Agreement',
    version: '2.0',
    content: `# Contractor Agreement

This agreement governs the terms of your engagement as an independent contractor.

## Scope of Work
Work will be assigned on a project basis with defined deliverables.

## Payment Terms
Payment is processed within 30 days of invoice submission.

## Intellectual Property
All work product created during engagement belongs to LUKHAS AI.`,
    requiredFor: ['contractor'],
    lastUpdated: '2024-01-05',
  },
  {
    id: 'manager-policy',
    title: 'Manager Policy Guide',
    version: '1.2',
    content: `# Manager Policy Guide

## Responsibilities
- Team performance management
- Resource allocation
- Employee development

## Reporting
- Weekly team status reports
- Monthly performance reviews
- Quarterly planning sessions`,
    requiredFor: ['manager'],
    lastUpdated: '2024-01-12',
  },
  {
    id: 'admin-policy',
    title: 'Administrator Policy',
    version: '1.0',
    content: `# Administrator Policy

## System Access
Administrators have elevated access to all systems.

## Security Requirements
- Multi-factor authentication required
- Regular security audits
- Access logging and monitoring

## Responsibilities
- User management
- System configuration
- Security incident response`,
    requiredFor: ['admin'],
    lastUpdated: '2024-01-08',
  },
]

export function getPoliciesForRole(role: UserRole): Policy[] {
  return POLICIES.filter((policy) => policy.requiredFor.includes(role))
}

export function getPolicyById(id: string): Policy | undefined {
  return POLICIES.find((policy) => policy.id === id)
}

export function getPolicyAsText(id: string): string {
  const policy = getPolicyById(id)
  return policy?.content ?? ''
}
